'use client';

import { SectionContainer } from '../component/SectionContainer';
import { DialogPlugin, Image } from 'tdesign-react';
import { useLocale, useTranslations } from 'use-intl';
import {
  useIsExternalDomain,
  useMeetingConfig,
  useMeetingStatus,
} from '../context/MeetingConfigContext';
import { MeetingStatus } from '../type';
import { useEffect, useRef, useState } from 'react';
import {
  goLogin,
  reserveLive,
  useEnvGlobalContext,
  useMedia,
  useWechat,
} from '@ssv-website/hooks';
import {
  ShowBookingLiveModal,
  ShowCustomLoginTipsModal,
  ShowCustomLoginTipsZhModal,
  ShowRegistrationModal,
  ShowRepeatRegisterModal,
} from '../utils/customModal';

const RegisterButton = ({ className }: { className?: string }) => {
  const t = useTranslations('s2025');
  const isMobile = useMedia('mobile');
  const locale = useLocale();
  const isEn = locale === 'en';
  const { contextIntl } = useEnvGlobalContext();
  const { intlRegisterInfo } = useMeetingConfig();

  const handleRegister = () => {
    if (isEn) {
      const { isLogin, login, userInfo, refreshUserInfo } = contextIntl || {};
      const { hasRegistered } = userInfo || {};
      if (!isLogin) {
        const modal = ShowCustomLoginTipsModal({
          content:
            'You are not Logged in. To perform this action, please log in and try again.',
          isMobile,
          options: {
            onCancel() {
              modal.hide();
            },
            onConfirm() {
              login?.();
              modal.hide();
            },
          },
        });
        return;
      }
      if (hasRegistered) {
        ShowRepeatRegisterModal({ isMobile });
      } else {
        ShowRegistrationModal({
          onSubmitSuccess: () => {
            refreshUserInfo?.();
          },
          isMobile,
          data: intlRegisterInfo,
        });
      }
    } else {
      window.location.href = `${window.location.origin}/wj/to-sign-up?id=${process.env.NEXT_PUBLIC_S2025_ACTIVITY_ID}`;
    }
  };
  return (
    <div
      className={`w-[250px] h-[84px] leading-[84px] text-center text-[24px] font-400 bg-[url(https://cdn-digital.ssv.qq.com/upload//s2025/s-btn-bg-normal.png)]  
      bg-no-repeat bg-cover bg-[#3A8AFB] text-[#FFFFFF] hover:bg-[url(https://cdn-digital.ssv.qq.com/upload//s2025/s-btn-bg-hover.png)] active:bg-[url(https://cdn-digital.ssv.qq.com/upload//s2025/s-btn-bg-active)] mobile:w-[132px] mobile:h-[44px] mobile:leading-[44px] mobile:text-xs cursor-pointer ${className}`}
      onClick={() => {
        handleRegister();
      }}
    >
      {t('立即报名')}
    </div>
  );
};

const ReserveLiveButton = ({ className }: { className?: string }) => {
  const t = useTranslations('s2025');
  const commonT = useTranslations('common');
  const isMobile = useMedia('mobile');
  const mainConfig = useMeetingConfig();
  const isExternalDomain = useIsExternalDomain();
  const { isWechat } = useWechat();
  const { contextIntl } = useEnvGlobalContext();

  const { liveQrCode, liveQrCodeWx, liveQrCodeH5, liveUrl } = mainConfig || {};

  let envType = 'pc';
  if (isMobile) {
    envType = 'h5';
    if (isWechat) envType = 'wx';
  }
  const qrCodeMap: Record<string, string> = {
    wx: liveQrCodeWx,
    h5: liveQrCodeH5,
    pc: liveQrCode,
  };

  const qrcode = qrCodeMap[envType];
  const handleBookingLiveVideo = () => {
    const confirmDia = DialogPlugin.confirm({
      body: (
        <div className="flex flex-col justify-center items-center px-10 gap-8">
          <Image
            src={qrcode}
            className="w-[252px] min-h-[316px] !bg-transparent"
            loading={commonT('loading')}
            fit="scale-down"
            alt="live qrcode"
          ></Image>
        </div>
      ),
      footer: false,
      confirmBtn: null,
      cancelBtn: null,
      className: 'rsCustomDialog',
      width: isMobile ? '320px' : 'auto',
      preventScrollThrough: true,
      onConfirm: ({ e }) => {
        confirmDia.hide();
      },
      onClose: ({ e, trigger }) => {
        confirmDia.hide();
      },
    });
  };
  const handleBookingLiveVideoEn = () => {
    const { isLogin, login } = contextIntl || {};
    if (!isLogin) {
      const modal = ShowCustomLoginTipsModal({
        content:
          'The webcast link for the conference will be sent to your registered email address.',
        isMobile,
        options: {
          onCancel() {
            modal.hide();
          },
          onConfirm() {
            login?.();
            modal.hide();
          },
        },
      });
      return;
    }
    reserveLive();
    ShowBookingLiveModal({
      isMobile,
      liveUrl: liveUrl,
      options: {},
    });
  };

  return (
    <div
      className={`cursor-pointer min-w-0 w-[250px] h-[84px] leading-[84px] text-[24px] text-center font-300 bg-[linear-gradient(90deg,#FFF_0%,#FBFCFF_100%)] text-[#006DFF] hover:text-[#121C59] active:text-[#121C59] mobile:w-[132px] mobile:h-[44px] mobile:leading-[44px] mobile:text-xs opacity-90 ${className}`}
      onClick={() => {
        // customEvents.emit('onScrollForumModule', {});
        isExternalDomain
          ? handleBookingLiveVideoEn()
          : handleBookingLiveVideo();
      }}
    >
      {t('预约直播')}
    </div>
  );
};
const WatchLiveButton = ({ className }: { className?: string }) => {
  const t = useTranslations('s2025');
  const mainConfig = useMeetingConfig();
  const { liveUrl } = mainConfig || {};
  const isExternalDomain = useIsExternalDomain();
  const { contextIntl, context } = useEnvGlobalContext();
  const isMobile = useMedia('mobile');

  // 观看直播
  const handleSeeLiveVideo = () => {
    const { is_login } = context || {};
    if (!is_login) {
      const modal = ShowCustomLoginTipsZhModal({
        isMobile,
        content: '观看直播需要先登录',
        options: {
          onCancel() {
            modal.hide();
          },
          onConfirm() {
            goLogin();
            modal.hide();
          },
        },
      });
      return;
    }
    window.open(liveUrl, '_blank');
  };
  const handleSeeLiveVideoEn = () => {
    const { isLogin, login } = contextIntl || {};
    if (!isLogin) {
      const modal = ShowCustomLoginTipsModal({
        content: 'You need to log in to watch the conference live stream.',
        isMobile,
        options: {
          onCancel() {
            modal.hide();
          },
          onConfirm() {
            login?.();
            modal.hide();
          },
        },
      });
      return;
    }
    window.open(liveUrl, '_blank');
  };
  return (
    <div
      className={`w-[250px] h-[84px] leading-[84px] text-center text-[24px] font-400 bg-[url(https://cdn-digital.ssv.qq.com/upload//s2025/s-btn-bg-normal.png)]  
      bg-no-repeat bg-cover bg-[#3A8AFB] text-[#FFFFFF] hover:bg-[url(https://cdn-digital.ssv.qq.com/upload//s2025/s-btn-bg-hover.png)] active:bg-[url(https://cdn-digital.ssv.qq.com/upload//s2025/s-btn-bg-active)] mobile:w-[132px] mobile:h-[44px] mobile:leading-[44px] mobile:text-xs cursor-pointer ${className}`}
      onClick={() => {
        isExternalDomain ? handleSeeLiveVideoEn() : handleSeeLiveVideo();
      }}
    >
      {t('观看直播')}
    </div>
  );
};

const MeetingEndButton = ({ className }: { className?: string }) => {
  const t = useTranslations('s2025');
  return (
    <div
      className={`w-[280px] h-[84px] leading-[84px] text-center text-[24px] font-400 bg-[#3A8AFB4D] text-[#FFFFFFCC] mobile:w-[132px] mobile:h-[44px] mobile:leading-[44px] mobile:text-xs cursor-pointer ${className}`}
    >
      {t('已结束')}
    </div>
  );
};

const MainInfoPC = () => {
  const locale = useLocale();
  const status = useMeetingStatus();
  return (
    <div className="relative pt-[150px] mobile:hidden">
      {locale === 'en' ? (
        <>
          <div
            className="w-[439px] h-[176px]"
            style={{
              background:
                'url(https://cdn-digital.ssv.qq.com/upload//s2025/main-name-en.png) no-repeat center/ contain',
            }}
          ></div>

          {[MeetingStatus.Review].includes(status) ? (
            <div
              className="w-[487px] h-[122.5px] mt-5"
              style={{
                background:
                  'url(https://cdn-digital.ssv.qq.com/upload//s2025/main-name-review-en.png) no-repeat center/ contain',
              }}
            ></div>
          ) : (
            <div
              className="w-[502px] h-[96px] mt-5"
              style={{
                background:
                  'url(https://cdn-digital.ssv.qq.com/upload//s2025/main-name1-en.png) no-repeat center/ contain',
              }}
            ></div>
          )}

          <div
            className={`w-[528px] h-[120px] ${
              [MeetingStatus.Review].includes(status)
                ? 'mt-[100px]'
                : 'mt-[50px]'
            }`}
            style={{
              background: `url(https://cdn-digital.ssv.qq.com/upload//s2025/main-local-time1-en${
                [MeetingStatus.Completed].includes(status) ? '-end' : ''
              }.png) no-repeat center/ contain`,
            }}
          ></div>
        </>
      ) : (
        <>
          <div
            className="w-[638px] h-[247px]"
            style={{
              background:
                'url(https://cdn-digital.ssv.qq.com/upload//s2025/main-name.png) no-repeat center/cover',
            }}
          ></div>
          {/* 精彩回顾 */}
          {[MeetingStatus.Review].includes(status) && (
            <div
              className="w-[181px] h-[42px]"
              style={{
                background:
                  'url(https://cdn-digital.ssv.qq.com/upload//s2025/s-review-text.png) no-repeat center/cover',
              }}
            ></div>
          )}
          <div
            className={`w-[396px] h-[84px] ${
              [MeetingStatus.Review].includes(status)
                ? 'mt-[144px]'
                : 'mt-[119px]'
            } `}
            style={{
              background: `url(https://cdn-digital.ssv.qq.com/upload//s2025/main-local-time${
                [MeetingStatus.Completed].includes(status) ? '-end' : ''
              }.png) no-repeat center/cover`,
            }}
          ></div>
        </>
      )}

      {[
        MeetingStatus.NotStarted,
        MeetingStatus.InProgress,
        MeetingStatus.Completed,
      ].includes(status) && (
        <div className="flex mt-10 gap-[12px] mobile:gap-[8px]">
          {/* 未开始 */}
          {status === MeetingStatus.NotStarted && (
            <>
              <RegisterButton />
              <ReserveLiveButton />
            </>
          )}
          {/* 进行中 */}
          {status === MeetingStatus.InProgress && <WatchLiveButton />}

          {/* 已结束 */}
          {status === MeetingStatus.Completed && <MeetingEndButton />}
        </div>
      )}
    </div>
  );
};

const MainInfoMobile = () => {
  const locale = useLocale();
  const status = useMeetingStatus();
  return (
    <div className="relative pt-[120px] hidden mobile:block">
      <div
        className=" w-[109px] h-5"
        style={{
          background:
            'url(https://cdn-digital.ssv.qq.com/upload//s2025/main-t-m.png) no-repeat center/100% 100%',
        }}
      ></div>
      {locale === 'en' ? (
        <div
          className={`${
            status === MeetingStatus.Review
              ? 'w-[335px] h-[231px] mx-auto mt-[45px]'
              : 'w-[331px] h-[193px] mx-auto mt-[45px]'
          }`}
          style={{
            background: `url(https://cdn-digital.ssv.qq.com/upload//s2025/main-info-en-m${
              status === MeetingStatus.Completed
                ? '-end'
                : status === MeetingStatus.Review
                ? '-review1'
                : ''
            }.png) no-repeat center/ 100% 100%`,
          }}
        ></div>
      ) : (
        <div
          className={`${
            status === MeetingStatus.Review
              ? 'w-[335px] h-[192px] mx-auto mt-[45px]'
              : 'w-[304px] h-[150px] mx-auto mt-[88px] '
          }`}
          style={{
            background: `url(https://cdn-digital.ssv.qq.com/upload//s2025/main-info-m${
              status === MeetingStatus.Completed
                ? '-end'
                : status === MeetingStatus.Review
                ? '-review'
                : ''
            }.png) no-repeat center/100% 100%`,
          }}
        ></div>
      )}

      {[
        MeetingStatus.NotStarted,
        MeetingStatus.InProgress,
        MeetingStatus.Completed,
      ].includes(status) && (
        <div className="mt-[52px] flex justify-center gap-2">
          {/* 未开始 */}
          {status === MeetingStatus.NotStarted && (
            <>
              <RegisterButton className="flex-1" />
              <ReserveLiveButton className="flex-1" />
            </>
          )}

          {/* 进行中 */}
          {status === MeetingStatus.InProgress && (
            <WatchLiveButton className="mobile:flex-1" />
          )}

          {/* 已结束 */}
          {status === MeetingStatus.Completed && (
            <MeetingEndButton className="mobile:flex-1" />
          )}
        </div>
      )}
    </div>
  );
};

function FloatingComponent() {
  const status = useMeetingStatus();
  if (![MeetingStatus.NotStarted, MeetingStatus.InProgress].includes(status))
    return null;
  return (
    <div className="fixed bottom-0 transform-gpu left-0 right-0 bg-[#F9FBFF] border-t-[1px] border-[#D5DEFD] z-50 pb-safe-bottom">
      <div className="flex justify-center items-center h-[116px] gap-3 mobile:h-[65px] mobile:px-5">
        {/* 未开始 */}
        {status === MeetingStatus.NotStarted && (
          <>
            <RegisterButton className="mobile:flex-1" />
            <ReserveLiveButton className="mobile:flex-1 transform-gpu border border-solid border-[#006DFF] hover:border-[#121C59] active:border-[#121C59]" />
          </>
        )}
        {/* 进行中 */}
        {status === MeetingStatus.InProgress && (
          <WatchLiveButton className="mobile:flex-1" />
        )}
      </div>
    </div>
  );
}

export default function MainSection() {
  // 用于绑定 container 元素的 ref
  const containerRef = useRef(null);
  // 跟踪 container 是否在视口内
  const [isContainerVisible, setIsContainerVisible] = useState(true);

  useEffect(() => {
    // 创建 Intersection Observer 实例
    const observer = new IntersectionObserver(
      ([entry]) => {
        // 更新可见性状态
        setIsContainerVisible(entry.isIntersecting);
      },
      { threshold: 0.1 } // 元素可见度超过 10% 时视为“可见”
    );

    // 开始观察 container 元素
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // 组件卸载时停止观察，避免内存泄漏
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <SectionContainer>
      {/* 被观察的 container 元素 */}
      <div ref={containerRef}>
        <MainInfoPC />
        <MainInfoMobile />
      </div>

      {/* 当 container 不可见时，显示悬浮组件 */}
      {!isContainerVisible && <FloatingComponent />}
    </SectionContainer>
  );
}
